<template>
  <div class="visual-table-container">
    <div class="visual-table-wrapper" ref="tableWrapper">
      <table class="visual-table">
        <thead>
        <tr>
          <th v-for="(col, index) in columns" :key="index">{{ col.label }}</th>
        </tr>
        </thead>
        <tbody :style="effectiveRoll  ? scrollStyle : {}">
        <!-- 使用v-for循环渲染数据行 -->
        <tr v-for="(row, rowIndex) in effectiveData" :key="rowIndex" class="table-row">
          <td v-for="(col, colIndex) in columns" :key="colIndex">
            <template v-if="col.type === 'satisfaction'">
              <div class="progress-container">
                <div class="progress-bar"
                     :style="{ width: row[col.prop] + '%', backgroundColor: getSatisfactionColor(row[col.prop]) }">
                </div>
              </div>
              <span>{{ row[col.prop] }}%</span>
            </template>
            <template v-else-if="col.type === 'count'">
              <div style="width: 40%">
                {{ formatNumber(row[col.prop]) }}
              </div>
            </template>
            <template v-else>
              {{ row[col.prop] }}
            </template>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    columns: {
      type: Array,
      required: true,
      default: () => []
    },
    data: {
      type: Array,
      required: true,
      default: () => []
    },

    roll: {
      type: Boolean,
      required: true,
      default: false
    },
    minScrollRows: {
      type: Number,
      default: 8 // 默认8条数据就启用滚动
    }
  },
  data() {
    return {
      scrollPosition: 0,
      animationId: null,
      displayData: [...this.data, ...this.data] // 复制数据实现无缝滚动
    }
  },
  watch: {
    // 监听roll prop变化
    roll(newVal) {
      if (newVal) {
        this.startAutoScroll();
      } else {
        this.stopAutoScroll();
      }
    },
    // 监听数据变化
    data() {
      this.$nextTick(() => {
        this.updateWrapperHeight();
      });
    },
  },
  computed: {
    scrollStyle() {
      return {
        transform: `translateY(-${this.scrollPosition}px)`
      }
    },
    rowHeight() {
      return 48 // 根据实际行高调整
    },
    totalScrollHeight() {
      return this.data.length * this.rowHeight
    },
    effectiveRoll() {
      // 如果props.roll为false，或者内容高度不足，则不滚动
      return this.roll && this.needScroll;
    },
    // 计算实际显示的数据
    effectiveData() {
      return this.needScroll ? [...this.data, ...this.data] : this.data;
    },
    // 判断是否需要滚动效果
    needScroll() {
      // 当数据量达到minScrollRows或内容高度超过容器时启用滚动
      return this.roll &&
          (this.data.length >= this.minScrollRows ||
              this.data.length * this.rowHeight > this.wrapperHeight);
    }
  },
  methods: {
    getSatisfactionColor(value) {
      if (value >= 90) return '#4ade80';
      if (value >= 75) return '#a3e635';
      if (value >= 60) return '#fbbf24';
      return '#ef4444';
    },
    formatNumber(num) {
      return Number(num).toLocaleString();
    },
    startAutoScroll() {
      this.stopAutoScroll();
      const animate = () => {
        this.scrollPosition += 0.3;
        if (this.scrollPosition >= this.totalScrollHeight) {
          this.scrollPosition = 0;
        }

        this.animationId = requestAnimationFrame(animate);
      };

      this.animationId = requestAnimationFrame(animate);
    },
    stopAutoScroll() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
    },
    handleMouseEnter() {
      this.stopAutoScroll();
    },
    handleMouseLeave() {
      this.startAutoScroll();
    },
    updateWrapperHeight() {
      if (this.$refs.tableWrapper) {
        this.wrapperHeight = this.$refs.tableWrapper.clientHeight;
      }
    },
  },
  mounted() {
    this.startAutoScroll();
    this.updateWrapperHeight();
    // 添加resize监听
    window.addEventListener('resize', this.updateWrapperHeight);
    this.$refs.tableWrapper.addEventListener('mouseenter', this.handleMouseEnter);
    this.$refs.tableWrapper.addEventListener('mouseleave', this.handleMouseLeave);
  },
  beforeDestroy() {
    this.stopAutoScroll();
    window.removeEventListener('resize', this.updateWrapperHeight);
    this.$refs.tableWrapper.removeEventListener('mouseenter', this.handleMouseEnter);
    this.$refs.tableWrapper.removeEventListener('mouseleave', this.handleMouseLeave);
  }
}
</script>

<style scoped>
.visual-table-container {
  background-color: #111b27;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.visual-table-wrapper {
  max-height: 400px;
  overflow: hidden;
  position: relative;
}

.visual-table {
  width: 100%;
  border-collapse: separate; /* 修改为separate确保边框可见 */
  border-spacing: 0;
  font-size: 14px;
  color: white;
}

.visual-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #1f2937;
}

th, td {
  padding: 12px 16px;
  text-align: left;
  position: relative; /* 为伪元素定位做准备 */
  border-bottom: none; /* 移除原生边框 */
  width: auto;
}

/* 使用伪元素实现边框线 */


th {
  color: #ccc;
  font-weight: bold;
  width: auto;
}

/* 最后一行不需要边框 */
.table-row:last-child td::after {
  display: none;
}

.progress-container {
  height: 12px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  overflow: hidden;
  margin-right: 10px;
  display: inline-block;
  width: 120px;
}

.progress-bar {
  height: 100%;
  transition: width 0.5s ease-in-out;
}

@media (max-width: 768px) {
  .visual-table-wrapper {
    max-height: none;
  }

  .visual-table, thead, tbody, th, td, tr {
    display: block;
  }

  th, td {
    padding: 10px;
  }

  th {
    background-color: transparent;
    font-weight: normal;
    position: static;
    width: auto;
  }

  tr {
    margin-bottom: 15px;
    position: relative;
  }

  /* 移动端边框调整 */
  .table-row td::after {
    left: 10px;
    right: 10px;
  }
}

.index {
  background-color: red;
}
</style>
